// Scroll wrapper for the active-bucket mode list: keep overflow scrolling,
// hide the native scrollbar, and hint clipped content with top/bottom blur
// fades + chevrons (below BucketSwitcher / above Keep sidebar open).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronsDown, ChevronsUp } from "lucide-react";

const SCROLL_EPS_PX = 2;

export function SidebarModeScrollArea({ children }: { children: ReactNode }) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      setCanScrollUp(el.scrollTop > SCROLL_EPS_PX);
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      setCanScrollDown(remaining > SCROLL_EPS_PX);
    };

    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);

    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, []);

  return (
    <div className="relative min-h-0 min-w-0 flex-1">
      <div
        ref={scrollRef}
        className={
          "h-full min-w-0 overflow-y-auto overflow-x-hidden " +
          "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        }
      >
        {children}
      </div>
      {canScrollUp ? (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 flex h-1/4 flex-col items-center justify-start pt-1"
          aria-hidden
        >
          <div
            className={
              "absolute inset-0 bg-gradient-to-b from-white via-white/70 to-transparent " +
              "backdrop-blur-[2px] dark:from-zinc-950 dark:via-zinc-950/70"
            }
          />
          <ChevronsUp
            className="relative z-10 h-4 w-4 text-gray-400 dark:text-zinc-500"
            strokeWidth={2}
          />
        </div>
      ) : null}
      {canScrollDown ? (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 flex h-1/4 flex-col items-center justify-end pb-1"
          aria-hidden
        >
          <div
            className={
              "absolute inset-0 bg-gradient-to-t from-white via-white/70 to-transparent " +
              "backdrop-blur-[2px] dark:from-zinc-950 dark:via-zinc-950/70"
            }
          />
          <ChevronsDown
            className="relative z-10 h-4 w-4 text-gray-400 dark:text-zinc-500"
            strokeWidth={2}
          />
        </div>
      ) : null}
    </div>
  );
}
